// Gói J — Nhật ký hệ thống (chỉ super): bảng audit lọc theo tài khoản/hành động/đối tượng, phân trang "Tải thêm".
// V12 (rà soát 05/10/2026): tên hành động tiếng Việt (mã gốc ở title), mặc định ẨN đăng nhập/đăng xuất (lọc ngay trên máy chủ để trang
// vẫn đủ 50 dòng), chi tiết + đối tượng đổi mã → tên kịch bản / bot / tài khoản / lệnh (GET /api/audit/ten).
import { api, buildQuery, errMsg } from "../api";
import { busy, clear, gen, h } from "../dom";
import { fmtTime } from "../format";
import { isSuper } from "../store";
import type { AuditItem, Paged, UserWithBots } from "../types";
import { chiTietDeDoc, doiTuongDeDoc, maCanTra, maTuTen, TEN_HANH_DONG, tenHanhDong, type TenMa } from "../ten-hanh-dong";
import { noPermission } from "./users";

export async function renderAudit(root: HTMLElement, _p: Record<string, string>, query: URLSearchParams): Promise<() => void> {
  if (!isSuper()) return noPermission(root, "Nhật ký hệ thống");
  const alive = gen("audit");
  let users: UserWithBots[] = [];
  try { users = await api<UserWithBots[]>("GET", "/api/users"); } catch { /* ô lọc user trống, bảng vẫn hiện */ }
  if (!alive()) return () => {};

  const userSel = h("select", { title: "Chỉ hiện thao tác của tài khoản này", "data-testid": "a-user" }, h("option", { value: "" }, "Mọi tài khoản"), ...users.map((u) => h("option", { value: u.id }, u.username)));
  const dsHd = h("datalist", { id: "a-action-ds" }, ...[...new Set(Object.values(TEN_HANH_DONG))].sort((a, b) => a.localeCompare(b, "vi")).map((t) => h("option", { value: t })));
  const action = h("input", { list: "a-action-ds", placeholder: "ví dụ: Đăng nhập, Sửa bot, Ra lệnh chạy", title: "Gõ hoặc chọn tên hành động (khớp đúng); gõ mã (vd bot.patch) cũng được", "data-testid": "a-action" });
  const dangNhap = h("input", { type: "checkbox", title: "Mặc định ẩn các dòng đăng nhập/đăng xuất cho dễ đọc; tick để hiện cả chúng", "data-testid": "a-login" });
  dangNhap.checked = query.get("dangNhap") === "1";
  const target = h("input", { placeholder: "mã bot / tài khoản / lệnh", title: "Mã (id) của bot, tài khoản hoặc lệnh bị tác động", "data-testid": "a-target" });
  userSel.value = query.get("userId") ?? "";
  action.value = query.get("action") ? tenHanhDong(query.get("action")!) : "";
  target.value = query.get("target") ?? "";
  const applyBtn = h("button", { type: "button", class: "pri", title: "Tải lại bảng theo bộ lọc bên trên", "data-testid": "a-apply" }, "Lọc");
  const tbody = h("tbody", { "data-testid": "audit-rows" });
  const more = h("button", { type: "button", hidden: true, title: "Tải thêm 50 dòng cũ hơn vào bảng", "data-testid": "a-more" }, "Tải thêm");
  const info = h("p", { class: "muted small", "data-testid": "audit-info" });
  const errBox = h("div", { class: "err-box", hidden: true, "data-testid": "page-error" });

  root.append(h("h1", {}, "Nhật ký hệ thống"),
    h("div", { class: "card" }, h("div", { class: "field-row" },
      h("label", { class: "f" }, h("span", {}, "Tài khoản"), userSel),
      h("label", { class: "f" }, h("span", {}, "Hành động"), action, dsHd),
      h("label", { class: "f" }, h("span", {}, "Đối tượng"), target)),
      h("div", { class: "row" }, applyBtn, h("label", { class: "inline", title: "Mặc định ẩn đăng nhập/đăng xuất (mỗi lần ai đó đăng nhập là một dòng)" }, dangNhap, "Hiện cả đăng nhập / đăng xuất"))),
    info, errBox,
    h("div", { class: "tbl" }, h("table", { "data-testid": "audit-table" },
      h("thead", {}, h("tr", {}, h("th", {}, "Lúc"), h("th", { title: "Ai làm; \"hệ thống\" = máy chủ tự làm" }, "Tài khoản"), h("th", { title: "Việc đã làm — di chuột lên để xem mã gốc" }, "Hành động"), h("th", { title: "Kịch bản / bot / tài khoản / lệnh bị tác động — di chuột lên để xem mã" }, "Đối tượng"), h("th", {}, "Chi tiết"))),
      tbody)),
    h("p", {}, more));

  let nextBefore: string | null = null;
  let count = 0;
  const filters = () => ({ userId: userSel.value || undefined, action: maTuTen(action.value) || undefined, target: target.value.trim() || undefined, boDangNhap: dangNhap.checked ? undefined : 1 });
  const ten: TenMa = {};
  /** Tra tên cho các mã chưa biết của một trang (tối đa 300 mã mỗi lần hỏi). Lỗi → giữ mã, báo một dòng (bảng vẫn hiện). */
  async function traTen(items: AuditItem[]): Promise<string | null> {
    const can = [...new Set(items.flatMap((a) => maCanTra(a.target, a.json)))].filter((m) => !(m in ten));
    try {
      for (let i = 0; i < can.length; i += 300) Object.assign(ten, (await api<{ ten: TenMa }>("GET", `/api/audit/ten${buildQuery({ ids: can.slice(i, i + 300).join(",") })}`)).ten);
      return null;
    } catch (e) { return `Không tra được tên kịch bản/bot (đang hiện mã): ${errMsg(e)}`; }
  }
  function row(a: AuditItem): HTMLTableRowElement {
    // Chi tiết: "Nhãn: giá trị · …" — chuỗi hiện nguyên (textContent, không escape), mã đổi ra tên.
    const detail = chiTietDeDoc(a.json, ten);
    return h("tr", { "data-testid": "audit-row", dataset: { action: a.action } },
      h("td", { class: "nowrap small" }, fmtTime(a.at)),
      h("td", { class: "nowrap" }, a.user ? h("span", { title: `Mã tài khoản: ${a.user.id}` }, a.user.username) : h("span", { class: "muted", title: "Máy chủ tự thực hiện, không do tài khoản nào" }, "hệ thống")),
      h("td", { title: `Mã: ${a.action}`, "data-testid": "audit-action" }, tenHanhDong(a.action)),
      h("td", { class: "small wrap", title: a.target ? `Mã: ${a.target}` : "", "data-testid": "audit-target" }, doiTuongDeDoc(a.target, ten)),
      h("td", { class: "small wrap muted", style: "max-width:420px" }, detail.length > 300 ? detail.slice(0, 300) + "…" : detail));
  }
  async function loadPage(reset: boolean): Promise<void> {
    if (reset) { clear(tbody); nextBefore = null; count = 0; }
    errBox.hidden = true;
    const g = gen("audit-load");
    try {
      const p = await api<Paged<AuditItem>>("GET", `/api/audit${buildQuery({ ...filters(), limit: 50, before: nextBefore })}`);
      if (!g() || !alive()) return;
      const loiTen = await traTen(p.items);
      if (!g() || !alive()) return;
      for (const a of p.items) tbody.append(row(a));
      count += p.items.length;
      nextBefore = p.nextBefore;
      more.hidden = !nextBefore;
      info.textContent = (count ? `${count} dòng${nextBefore ? " (còn nữa)" : ""}` : "Không có dòng nào khớp bộ lọc.") + (loiTen ? ` · ${loiTen}` : "");
    } catch (e) {
      if (!g() || !alive()) return;
      errBox.textContent = `Không tải được nhật ký: ${errMsg(e)}`;
      errBox.hidden = false;
      info.textContent = "";
    }
  }
  const apDung = (): Promise<void> => {
    history.replaceState(null, "", `#/audit${buildQuery({ userId: userSel.value, action: maTuTen(action.value), target: target.value.trim(), dangNhap: dangNhap.checked ? 1 : undefined })}`);
    return loadPage(true);
  };
  applyBtn.addEventListener("click", () => void busy(applyBtn, "Đang lọc…", apDung));
  // Tick/bỏ tick là lọc lại ngay (kể cả khi trang trước chưa tải xong — gen("audit-load") bỏ kết quả cũ).
  dangNhap.addEventListener("change", () => void apDung());
  more.addEventListener("click", () => void busy(more, "Đang tải…", () => loadPage(false)));
  await loadPage(true);
  renderServerLogs(root);
  return () => {};
}

// Nhật ký máy chủ (console 500 dòng gần nhất, giữ trong bộ nhớ) — để tự thấy nguyên nhân khi bot lỗi/502 mà không cần SSH.
interface LogLine { at: string; level: "log" | "warn" | "error"; text: string }
function renderServerLogs(root: HTMLElement): void {
  const alive = gen("server-logs");
  const level = h("select", { title: "Chỉ hiện dòng ở mức này: tất cả, cảnh báo trở lên, hoặc chỉ lỗi", "data-testid": "log-level" },
    h("option", { value: "all" }, "Mọi dòng"), h("option", { value: "warn" }, "Cảnh báo + lỗi"), h("option", { value: "error" }, "Chỉ lỗi"));
  const q = h("input", { type: "search", placeholder: "tìm chữ trong nhật ký…", maxlength: 200, title: "Chỉ hiện dòng chứa chữ này (không phân biệt hoa thường)", "data-testid": "log-q" });
  const reload = h("button", { type: "button", title: "Tải lại nhật ký máy chủ mới nhất", "data-testid": "log-reload" }, "Tải lại");
  const info = h("p", { class: "muted small", "data-testid": "log-info" }, "Đang tải…");
  const pre = h("pre", { class: "server-log", "data-testid": "log-pre" });
  const card = h("div", { class: "card", "data-testid": "server-logs" }, h("h3", {}, "Nhật ký máy chủ (500 dòng gần nhất)"),
    h("p", { class: "muted small" }, "Dòng console của tiến trình Boss từ lúc khởi động (mất khi khởi động lại). Tìm ở đây trước khi vào máy chủ đọc journalctl."),
    h("div", { class: "row" }, h("div", { class: "grow bot-search-wrap" }, q), level, reload), info, pre);
  root.append(card);
  async function load(): Promise<void> {
    const g = gen("server-logs-load");
    try {
      const r = await api<{ items: LogLine[]; total: number; capacity: number }>("GET", `/api/logs${buildQuery({ level: level.value === "all" ? undefined : level.value, q: q.value.trim() || undefined, limit: 500 })}`);
      if (!g() || !alive()) return;
      clear(pre);
      if (!r.items.length) { info.textContent = "Không có dòng nào khớp."; return; }
      info.textContent = `${r.items.length} dòng (bộ đệm giữ tối đa ${r.capacity})`;
      for (const l of r.items) pre.append(h("div", { class: `log-line ${l.level}`, "data-testid": "log-line", dataset: { level: l.level } }, h("span", { class: "log-at" }, fmtTime(l.at)), " ", h("span", { class: "log-lv" }, l.level === "error" ? "LỖI" : l.level === "warn" ? "CẢNH BÁO" : "thông tin"), " ", l.text));
      pre.scrollTop = pre.scrollHeight;
    } catch (e) {
      if (!g() || !alive()) return;
      info.textContent = `Không tải được nhật ký máy chủ: ${errMsg(e)}`;
    }
  }
  reload.addEventListener("click", () => void busy(reload, "…", load));
  level.addEventListener("change", () => void load());
  q.addEventListener("change", () => void load());
  void load();
}
